import { useCallback, useState } from "react";
import * as React from "react";
import { compact } from "es-toolkit";

import { niceNumber, niceDateTime, niceNumberEstimate, dateFromWeekShift, TIME_RANGES } from "@/lib/util";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import * as rt from "@/lib/simulationReportModels";
import translatedMaterialTitle from "@/modules/shared/translatedMaterialTitle";
import { MATERIAL_TAGS_ACCESS, ORDER_TAGS, ORDER_TAGS_ACCESS } from "@/lib/ManufacturingTags.generated";
import { ORDER_STATUS } from "@/lib/ManufacturingConsts.generated";
import * as r from "@/lib/backend/reports.generated.types";

import { getBackend } from "@/lib/backend/backend2";
import { useGetOrderComment } from "@/lib/custom-hooks/queryComment";

import { ORDER_TYPE } from "@/lib/ManufacturingConsts.generated";
import { dateFromWeekAndShift, addHours } from "@/lib/util";
import OrderBadges, { ORDER_BADGE_TYPES } from "../../../OrderBadges";

import type { IOrderProducedModelEx } from "../../reducers/linesOrders";
import type { IMaterialModel } from "@/lib/backend/manufacturing2.generated.types";
import type { ITags } from "@/lib/models";

type TooltipOrderData = {
    order_id: string;
    external_id: string;
    material_external_id: string;
    material_title: string;
    quantity_total: number;
    quantity_produced: number;
    quantity_produced_fixed: number;
    quantity_produced_next: number;
    measurement_unit: string;
    process_num: string;
    process_title: string;
    failed_scheduling: boolean;
    insight_freeze_order: boolean;
    freeze_time_end: number;
    capacity_factor: number;
    model_slope: number;
    model_name: string;
    model_normative_fallback: boolean;
    estimated_start_h: number;
    estimated_completion_h: number;
    earliest_start: number;
    earliest_end: number;
    initial_earliest_start: number;
    initial_earliest_end: number;
    order_type: string;
};

const getUnscheduledOrderData = (
    order: IOrderProducedModelEx,
    current_shift: number,
    unscheduled_align_earliest_end: boolean
): TooltipOrderData => {
    const estimated_duration = order.estimated_duration ?? 0;
    let estimated_start_h = 0;
    if (current_shift >= 0) {
        const current_shift_start_ts = dateFromWeekShift(new Date(), current_shift).getTime();
        estimated_start_h =
            (unscheduled_align_earliest_end
                ? order.earliest_end - estimated_duration * TIME_RANGES.HOUR - current_shift_start_ts
                : order.earliest_start - current_shift_start_ts) / TIME_RANGES.HOUR;
    }

    if (estimated_start_h < 0) {
        estimated_start_h = 0;
    }

    const estimated_completion_h = estimated_start_h + estimated_duration;

    return {
        external_id: order.external_id,
        material_external_id: order.material_external_id,
        material_title: order.material_title,
        quantity_total: order.quantity_total,
        measurement_unit: order.measurement_unit,
        quantity_produced: order.quantity_produced,
        quantity_produced_fixed: order.quantity_produced,
        quantity_produced_next: 0,
        process_num: order.process_num,
        process_title: order.title || "",
        failed_scheduling: false,
        insight_freeze_order: false,
        freeze_time_end: 0,
        order_id: order.uuid,
        capacity_factor: order.capacity_factor,
        model_slope: 0,
        model_name: "",
        model_normative_fallback: false,
        estimated_start_h,
        estimated_completion_h,
        earliest_start: order.earliest_start,
        earliest_end: order.earliest_end,
        initial_earliest_start: order.initial_earliest_start,
        initial_earliest_end: order.initial_earliest_end,
        order_type: order.order_type,
    };
};

const getSimulationOrderData = (order: rt.ISimulationReportOrderEx): TooltipOrderData => {
    return {
        external_id: order.order_external_id,
        material_external_id: order.material,
        material_title: order.material_title,
        quantity_total: order.quantity_total,
        measurement_unit: order.measurement_unit,
        quantity_produced: order.quantity_produced,
        quantity_produced_fixed: order.quantity_produced_fixed,
        quantity_produced_next: order.quantity_produced_next,
        process_num: order.process_num + "",
        process_title: order.process_title,
        failed_scheduling: order.failed_scheduling,
        insight_freeze_order: order.insight_freeze_order,
        freeze_time_end: order.freeze_time_end,
        order_id: order.order_id,
        capacity_factor: order.capacity_factor,
        model_slope: order.model_slope,
        model_name: order.model_name,
        model_normative_fallback: order.model_normative_fallback,
        estimated_start_h: order.estimated_start[1].val,
        estimated_completion_h: order.estimated_completion[1].val,
        earliest_start: new Date(order.earliest_start).getTime(),
        earliest_end: new Date(order.earliest_end).getTime(),
        initial_earliest_start: new Date(order.initial_earliest_start).getTime(),
        initial_earliest_end: new Date(order.initial_earliest_end).getTime(),
        order_type: order.order_type,
    };
};

type TooltipOrderCommentProps = {
    order_uuid: string;
};

const TooltipOrderCommentValue = ({ order_uuid }: TooltipOrderCommentProps) => {
    const { data: comment, isLoading, isError } = useGetOrderComment(order_uuid);
    const text = isError ? translate("common.error") : comment || "-";

    return (
        <tr>
            <th>{translate("common.comment", "Comment")}</th>
            <td>{isLoading ? <i className="fas fa-spinner fa-spin" /> : text}</td>
        </tr>
    );
};

const ProducedPerHourValue = (props: { order_data: TooltipOrderData }) => {
    const { order_data } = props;
    return (
        <>
            {niceNumberEstimate(order_data.model_slope !== 0 ? 1 / order_data.model_slope : 0)}{" "}
            {order_data.measurement_unit} (
            {order_data.model_normative_fallback
                ? translate("common.norm", "Norm")
                : translate("common.model", "Model")}
            {order_data.model_normative_fallback && order_data.model_name !== "" && (
                <i className="fas fa-info-circle ml-1" title={order_data.model_name} />
            )}
            ){" "}
            {order_data.capacity_factor !== 1 && (
                <sup
                    className="fas fa-info-circle"
                    title={`${translate("common.capacity", "Capacity")}: ${Math.round(order_data.capacity_factor * 100)}%`}
                />
            )}
        </>
    );
};

const TooltipOrderQuantityValue = (props: { order_data: TooltipOrderData; is_historical: boolean }) => {
    const { order_data, is_historical } = props;
    const quantity = is_historical
        ? order_data.quantity_produced
        : order_data.quantity_produced_fixed + order_data.quantity_produced_next;
    const quantity_tooltip_parts: string[] = [];
    if (!is_historical) {
        if (order_data.quantity_produced_fixed !== order_data.quantity_produced) {
            quantity_tooltip_parts.push(
                `${translate(
                    "common.tooltip_microplan_quantity_fixed",
                    "Quantity increased based on realization of subsequent operations"
                )}: ` + `${order_data.quantity_produced}=>${order_data.quantity_produced_fixed}`
            );
        }

        if (order_data.quantity_produced_next > 0) {
            quantity_tooltip_parts.push(`${translate("Manufacturing.MicroplanSummary.done", "Done")}:`);
            quantity_tooltip_parts.push(
                ` - ${translate("common.previous_shifts", "Previous shifts")}: ${niceNumber(order_data.quantity_produced_fixed, 0)}`
            );
            quantity_tooltip_parts.push(
                ` - ${translate("common.current_shift", "Current shift")}: ${niceNumber(order_data.quantity_produced_next, 0)}`
            );
        }
    }

    const tooltip = quantity_tooltip_parts.length > 0 ? quantity_tooltip_parts.join("\n") : null;

    return (
        <>
            <span>
                {niceNumber(quantity, 0)} {tooltip !== null && <sup className="fas fa-info-circle" title={tooltip} />}
            </span>{" "}
            / {niceNumber(order_data.quantity_total, 0)} {order_data.measurement_unit}
        </>
    );
};

export type TooltipProperties = { name: string; value: string };
export type OrderTooltipProps = {
    order?: rt.ISimulationReportOrderEx | null;
    unscheduled_order?: IOrderProducedModelEx | null;
    insight_tooltips: React.ReactNode[];
    properties: TooltipProperties[];
    material?: IMaterialModel;
    tags: ITags;
    custom_title: string;
    is_historical: boolean;
    from_shift_time?: r.ISimulationReportShiftTime;
    original_earliest_start?: number;
    original_earliest_end?: number;
    original_initial_earliest_start?: number;
    original_initial_earliest_end?: number;
    unscheduled_align_earliest_end?: boolean;
};

const OrderTooltip = (props: OrderTooltipProps) => {
    const {
        order,
        unscheduled_order = null,
        insight_tooltips = [],
        properties = [],
        material,
        tags,
        custom_title,
        is_historical,
        from_shift_time,
        original_earliest_start,
        original_earliest_end,
        unscheduled_align_earliest_end = false,
    } = props;
    const [collective_orders, setCollectiveOrders] = useState<string[] | null>(null);
    let order_data: TooltipOrderData | null = null;

    const handleBadgeClick = useCallback(
        async (event: React.MouseEvent<HTMLSpanElement>) => {
            event.stopPropagation();
            const target = event.currentTarget;
            if (target.dataset.id === ORDER_BADGE_TYPES.collective_order) {
                if (collective_orders !== null) {
                    return;
                }

                try {
                    const res = await getBackend().manufacturing.getOrdersSimple({
                        tags_obj: {
                            [ORDER_TAGS.leading_order]: leading_order_id,
                        },
                        status: ORDER_STATUS.open,
                    });
                    const order_ids = new Set<string>(res.operations.map(o => o.external_id));
                    setCollectiveOrders(Array.from(order_ids));
                } catch (error) {
                    console.error("Failed to load collective orders", error);
                }
            }
        },
        [collective_orders, tags]
    );

    const renderRow = useCallback(
        (label: string, value: React.ReactNode, key?: string) => (
            <tr key={key ?? label}>
                <th>{label}</th>
                <td>{value}</td>
            </tr>
        ),
        []
    );

    if (unscheduled_order) {
        order_data = getUnscheduledOrderData(
            unscheduled_order,
            from_shift_time !== undefined ? from_shift_time.shift_number : -1,
            unscheduled_align_earliest_end
        );
    } else if (order) {
        order_data = getSimulationOrderData(order);
    } else {
        return null;
    }

    let material_external_id = order_data.material_external_id;
    if (
        material !== undefined &&
        MATERIAL_TAGS_ACCESS.make_to_order(material.tags) &&
        MATERIAL_TAGS_ACCESS.external_parent_material_id(material.tags)
    ) {
        material_external_id = MATERIAL_TAGS_ACCESS.external_parent_material_id(material.tags);
    }
    const is_unreleased = !ORDER_TAGS_ACCESS.system_status_released(tags);

    let time_start: Date | null = null;
    let time_end: Date | null = null;

    if (from_shift_time) {
        const current_time = dateFromWeekAndShift(
            from_shift_time.week,
            from_shift_time.year,
            from_shift_time.shift_number
        );
        time_start = addHours(current_time, order_data.estimated_start_h);
        time_end = addHours(current_time, order_data.estimated_completion_h);
    }

    const priority = tags[ORDER_TAGS.priority];
    const order_type = order_data.order_type;

    const leading_order_id = ORDER_TAGS_ACCESS.leading_order(tags);
    const required_deadline = ORDER_TAGS_ACCESS.required_deadline(tags);
    const target_sales_order_customer = ORDER_TAGS_ACCESS.target_sales_order_customer(tags);
    const target_sales_order_quantity = ORDER_TAGS_ACCESS.target_sales_order_quantity(tags);
    const end_date_as_unscheduled = ORDER_TAGS_ACCESS.end_date_as_unscheduled(tags);

    const tableRows: React.ReactNode[] = compact([
        renderRow(translate("Manufacturing.Planning.ident", "Ident"), material_external_id),
        renderRow(translate("common.process", "Process"), `${order_data.process_num} - ${order_data.process_title}`),
        renderRow(
            translate("common.order", "Order"),
            <>
                {order_data.external_id}{" "}
                <OrderBadges
                    order_type={[ORDER_TYPE.plan].find(t => t === order_type)}
                    is_unreleased={is_unreleased}
                    priority={priority}
                    order_id={order_data.external_id}
                    leading_order_id={leading_order_id}
                    onClick={handleBadgeClick}
                />
            </>
        ),
        collective_orders !== null &&
            renderRow(
                translate("common.collective_order_group", "Collective order group"),
                <>
                    <strong>{leading_order_id}</strong>
                    {collective_orders.map((order_id, i) => {
                        if (order_id === leading_order_id) {
                            return null;
                        }

                        return <React.Fragment key={i}>, {order_id}</React.Fragment>;
                    })}
                </>
            ),
        renderRow(
            translate("common.material", "Material"),
            translatedMaterialTitle({
                material_external_id: order_data.material_external_id,
                material_title: order_data.material_title,
            })
        ),
        renderRow(
            translate("common.quantity", "Quantity"),
            <TooltipOrderQuantityValue order_data={order_data} is_historical={is_historical} />
        ),
        order != null &&
            unscheduled_order === null &&
            renderRow(
                translate("common.produced_per_hour", "Produced per hour"),
                <ProducedPerHourValue order_data={order_data} />
            ),
        renderRow(
            translate("OrderTableProduction.time_start", "Start time"),
            time_start ? niceDateTime(new Date(time_start)) : "N/A"
        ),
        renderRow(
            translate("OrderTableProduction.time_end", "End time"),
            time_end ? niceDateTime(new Date(time_end)) : "N/A"
        ),
        !end_date_as_unscheduled &&
            renderRow(
                translate("OrderTableProduction.earliest_start"),
                niceDateTime(
                    new Date(
                        original_earliest_start !== undefined ? original_earliest_start : order_data.earliest_start
                    )
                )
            ),
        renderRow(
            translate("OrderTableProduction.earliest_end"),
            niceDateTime(
                new Date(original_earliest_end !== undefined ? original_earliest_end : order_data.earliest_end)
            )
        ),
        end_date_as_unscheduled &&
            renderRow(
                translate("OrderTableProduction.end_unscheduled"),
                niceDateTime(new Date(Number(end_date_as_unscheduled)))
            ),
        required_deadline > 0 &&
            renderRow(
                translate("common.deadline", "Deadline"),
                <>
                    {niceDateTime(new Date(required_deadline))}{" "}
                    {time_end !== null && required_deadline < time_end.getTime() && (
                        <i
                            className="fas fa-exclamation-circle text-danger"
                            title={translate(
                                "Manufacturing.InsightTypesSelect.operation_exceeds_deadline",
                                "Operation exceeds deadline"
                            )}
                        />
                    )}
                </>
            ),
        target_sales_order_customer !== "" &&
            renderRow(translate("common.target_sales_order_customer", "Customer"), target_sales_order_customer),
        target_sales_order_quantity > 0 &&
            renderRow(
                translate("common.target_sales_order_quantity", "Quantity for customer"),
                niceNumber(target_sales_order_quantity)
            ),
        ...properties.map((property, i) => renderRow(property.name, property.value, `property-${i}`)),
        <TooltipOrderCommentValue key="comment" order_uuid={order_data.order_id} />,
    ]);

    return (
        <>
            {order_data.failed_scheduling && (
                <p className="order-tooltip-title">{translate("common.failed_scheduling", "Failed scheduling")}</p>
            )}
            {custom_title !== "" && <p className="order-tooltip-title">{custom_title}</p>}
            <table className="order-tooltip-properties">
                <tbody>{tableRows}</tbody>
            </table>
            {order_data.insight_freeze_order && (
                <p className="order-tooltip-section font-weight-500 mb-0">
                    {translate("common.order_is_frozen", "End of order is frozen at ")}{" "}
                    {niceDateTime(new Date(order_data.freeze_time_end))}
                </p>
            )}
            {insight_tooltips.length > 0 && (
                <ul className="order-tooltip-insights list-unstyled mb-0">
                    {insight_tooltips.map((tooltip, i) => (
                        <li key={i} className="order-tooltip-section">
                            {tooltip}
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
};

export default OrderTooltip;
